/* eslint-disable jsx-a11y/label-has-associated-control */
import React from "react";
import PropTypes from "prop-types";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import classNames from "classnames";
import utils from "@utils/index";
import { useGetDisputeById } from "@queries/NonRKPayInvoiceQueries";
import {
	AddJobsModal,
	ConvertEstimateModal,
	MissingTimecardModal,
	UpdateLineItemsModal,
	DeleteJobConfirmationModal,
	InvoiceConflictModal,
	EmailInvoiceModal,
	InternalNotesModal,
} from "../components";
import { ApplyPaymentsModal, ConfirmationModal } from "../../../../components";
import JobActivityLogModal from "../../../jobs/JobDetails/components/JobActivityLogModal/JobActivityLogModal";
import "./NonRKPayInvoiceDetail.scss";
import { EditTimecardModal } from "../../../jobs/JobDetails/components";
import AttachmentsModal from "../components/AttachmentsModal/AttachmentsModal";
import TimeCardNotesModal from "../components/TimeCardNotesModal/TimeCardNotesModal";
import NonRKPayInvoiceDetailForm from "./components/NonRKPayInvoiceDetailForm";
import NonRKPayInvoiceDetailView from "./components/NonRKPayInvoiceDetailView";
import useNonRKPayInvoiceDetailHook from "./useNonRKPayInvoiceDetailHook";
import NonRKPayToolbar from "./components/NonRKPayInvoiceToolbar/NonRKPayToolbar";
import RefundNonRKPayInvoiceEstimateModal from "./components/RefundNonRKPayInvoiceEstimateModal";
import DisputesMenu from "../components/DisputesMenu";
import GenerateEstimateInvoiceModal from "./components/GenerateEstimateInvoiceModal";
import { DisputeProvider } from "../context/DisputeContext";
import ConnectedInvoicesModal from "../components/ConnectedInvoicesModal/ConnectedInvoicesModal";

const NonRKPayInvoiceDetail = props => {
	const {
		user,
		user: { TimeZoneId },
		flags: { officerMiscFeesV3, clientV4InvoicingDisputes },
		router,
		location: { query },
	} = props;
	const { lang } = global;

	const officerMiscFeesV3Enabled = !!officerMiscFeesV3;
	const nonRKPayInvoiceDetailHook = useNonRKPayInvoiceDetailHook(props);

	const {
		invoiceState,
		refundsInTransit,
		missingTimecardModal,
		showConvertEstimateModal,
		showUpdateLineItemsModal,
		deletingOfficerViewJob,
		invoiceConflicts,
		confirmationModal,
		applyPaymentsModal,
		emailInvoiceModal,
		showAttachmentsModal,
		selectedAttachment,
		selectedNote,
		showTimeCardNotesModal,
		showInternalNotesModal,
		showJobActivityLogModal,
		showEditTimeCardForm,
		currentTimeCardItem,
		jobActivityLogModalLineItem,
		editTimecardAgencyInvoiceEnabled,
		toggleAttachmentsModal,
		setEmailInvoiceModal,
		setAppliedPayments,
		setShowUpdateLineItemsModal,
		setDeletingOfficerViewJob,
		setInvoiceConflicts,
		handleRemoveLineItem,
		onConfirmConvertEstimateToInvoice,
		updateLineItems,
		onConfirmMissingTimecardModal,
		onEditTimeCard,
		toggleTimeCardNotesModal,
		toggleInternalNotesModal,
		setShowJobActivityLogModal,
		handleOnSaveInternalNotes,
		addJobsModal,
		emailingInvoice,
		setShowEditTimeCardForm,
		onConfirmCancelConvertEstimate,
		onCancelMissingTimecardModal,
		showRefundNonRKPayInvoiceEstimateModal,
		setShowRefundNonRKPayInvoiceEstimateModal,
		handleRefundEstimate,
		showGenerateEstimateInvoiceModal,
		onConfirmGenerateEstimateInvoiceModal,
		setShowGenerateEstimateInvoiceModal,
		showConnectedInvoicesModal,
		toggleConnectedInvoicesModal,
		associatedInvoices,
	} = nonRKPayInvoiceDetailHook;

	const {
		archived,
		activeDisputeId,
		finalized,
		estimateFinalized,
		voided,
		electronicPaymentsMade,
		issueDate,
		isEstimate,
		invoiceId,
		billTo,
		invoiceVersion,
		lineItems,
		typeId,
	} = invoiceState;

	const { data: dispute } = useGetDisputeById(activeDisputeId, {
		enabled: !!activeDisputeId && clientV4InvoicingDisputes,
		onError: () => {
			utils.notify({
				type: "error",
				message: lang.t("Unable to load invoice disputes"),
			});
		},
	});

	const jobEventInvoicedTypeId = utils.mapInvoiceTypeIdToJobEventInvoicedType(typeId);

	const showDisputesMenu = Boolean(dispute) && clientV4InvoicingDisputes;

	const invoiceDetailsView =
		(invoiceVersion === 3 && !isEstimate && (archived || finalized || voided)) ||
		(invoiceVersion !== 3 && !isEstimate && (archived || voided || electronicPaymentsMade)) ||
		user.isSuperAdmin();

	const estimateDetailsView = isEstimate && (estimateFinalized || archived || voided);

	let clientAddress = billTo.streetAddress;
	if (
		billTo.streetAddress &&
		(billTo.city || billTo.stateAbbreviation || billTo.stateName || billTo.zip)
	)
		clientAddress += "\n";
	clientAddress += `${billTo.city || ""}${billTo.city && (billTo.stateAbbreviation || billTo.stateName) ? ", " : ""}`;
	clientAddress += `${billTo.stateAbbreviation || billTo.stateName}`;
	if ((billTo.city || billTo.stateAbbreviation || billTo.stateName) && billTo.zip)
		clientAddress += " ";
	clientAddress += billTo.zip;

	/** TODO:
	 * We need to determine if the disputes menu should be shown.
	 * using the `ActiveDisputeId` field in the invoice object.
	 * This field is not available in the current API response.
	 * Once it is available, we can use it to determine if the
	 * disputes menu should be shown.
	 */
	const invoiceDetailsContainerClassNames = classNames({
		"flex-2": true,
		"container-fluid": showDisputesMenu,
		"container": !showDisputesMenu,
	});

	const containerClassName = classNames({
		"page-container": showDisputesMenu,
	});

	return (
		<DisputeProvider>
			<NonRKPayToolbar
				clientV4InvoicingDisputes={clientV4InvoicingDisputes}
				nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
				router={router}
				isSuperAdmin={user.isSuperAdmin()}
				user={user}
			/>
			<div className={containerClassName}>
				{showDisputesMenu && (
					<div className="flex-1">
						<DisputesMenu
							dispute={dispute}
							disputeId={activeDisputeId}
							className="sticky padding-16 margin-top-20 bg-white"
						/>
					</div>
				)}
				<div
					className={invoiceDetailsContainerClassNames}
					id="nonrkpay-invoice-detail"
				>
					<Choose>
						<When condition={invoiceDetailsView || estimateDetailsView}>
							<NonRKPayInvoiceDetailView
								user={user}
								clientAddress={clientAddress}
								officerMiscFeesV3={officerMiscFeesV3Enabled}
								nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
							/>
						</When>
						<Otherwise>
							<NonRKPayInvoiceDetailForm
								clientAddress={clientAddress}
								user={user}
								officerMiscFeesV3={officerMiscFeesV3Enabled}
								clientV4InvoicingDisputes={clientV4InvoicingDisputes}
								nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
								query={query}
							/>
						</Otherwise>
					</Choose>

					{/* // Modals */}

					<AddJobsModal
						ref={addJobsModal}
						invoiceId={invoiceId}
						user={user}
						lineItems={lineItems}
						jobInvoicedTypeId={jobEventInvoicedTypeId}
						addCustomDateEnabled // this prop is passed to determine if the custom date range button should be shown or not.
						// Since this is the new component, we want to only show it here. Once we migrate from the old component we can remove this.
					/>
					<MissingTimecardModal
						settings={missingTimecardModal}
						timeZone={TimeZoneId}
						onSuccess={onConfirmMissingTimecardModal}
						onCancel={onCancelMissingTimecardModal}
					/>
					<ConvertEstimateModal
						show={showConvertEstimateModal}
						issueDate={issueDate}
						invoiceVersion={invoiceVersion}
						onConfirm={onConfirmConvertEstimateToInvoice}
						onCancel={onConfirmCancelConvertEstimate}
					/>

					<GenerateEstimateInvoiceModal
						show={showGenerateEstimateInvoiceModal}
						issueDate={issueDate}
						invoiceVersion={invoiceVersion}
						onConfirm={onConfirmGenerateEstimateInvoiceModal}
						onCancel={() => setShowGenerateEstimateInvoiceModal(false)}
					/>

					<UpdateLineItemsModal
						show={showUpdateLineItemsModal}
						onCancel={() => setShowUpdateLineItemsModal(false)}
						onConfirm={() => {
							updateLineItems();
							setShowUpdateLineItemsModal(false);
						}}
					/>
					<DeleteJobConfirmationModal
						show={deletingOfficerViewJob !== undefined}
						jobEventId={deletingOfficerViewJob}
						lineItems={lineItems}
						onCancel={() => setDeletingOfficerViewJob(undefined)}
						onConfirm={() => handleRemoveLineItem(deletingOfficerViewJob)}
					/>
					<InvoiceConflictModal
						show={invoiceConflicts.length > 0}
						onHide={() => setInvoiceConflicts([])}
						isEstimate={isEstimate}
						lineItems={invoiceConflicts}
					/>
					<ConfirmationModal ref={confirmationModal} />
					<ApplyPaymentsModal
						ref={applyPaymentsModal}
						callback={setAppliedPayments}
						user={user}
						invoiceVersion={invoiceVersion}
						refundsInTransit={refundsInTransit}
						disabledManualPayments={estimateFinalized}
					/>
					<EmailInvoiceModal
						billingEmail={billTo.email}
						show={emailInvoiceModal.show}
						title={emailInvoiceModal.title}
						type={emailInvoiceModal.type}
						message={emailInvoiceModal.message}
						confirmButtonText={emailInvoiceModal.confirmButtonText}
						onSubmit={emailInvoiceModal.onSubmit}
						loading={emailingInvoice}
						onCancel={() => setEmailInvoiceModal({ show: false })}
					/>
					{editTimecardAgencyInvoiceEnabled && (
						<EditTimecardModal
							show={showEditTimeCardForm}
							onHide={() => setShowEditTimeCardForm(false)}
							user={user}
							timecard={currentTimeCardItem}
							positionId={currentTimeCardItem?.JobId}
							clockInTime={currentTimeCardItem?.__computedData?.ClockInTime}
							clockOutTime={currentTimeCardItem?.__computedData?.ClockOutTime}
							notes={currentTimeCardItem?.Reason}
							onSave={onEditTimeCard}
							newDesign
						/>
					)}
					<AttachmentsModal
						invoiceId={query.id}
						show={showAttachmentsModal}
						onHide={() => toggleAttachmentsModal()}
						user={user}
						selected={selectedAttachment}
					/>

					<TimeCardNotesModal
						invoiceId={query.id}
						show={showTimeCardNotesModal}
						onHide={() => toggleTimeCardNotesModal()}
						user={user}
						selected={selectedNote}
					/>

					<InternalNotesModal
						invoiceId={query.id}
						show={showInternalNotesModal}
						onHide={() => toggleInternalNotesModal()}
						onSave={() => handleOnSaveInternalNotes()}
						user={user}
					/>

					{jobActivityLogModalLineItem != null && (
						<JobActivityLogModal
							onHide={() => setShowJobActivityLogModal(false)}
							show={showJobActivityLogModal}
							clockInTime={jobActivityLogModalLineItem.OfficerClockIn}
							clockOutTime={jobActivityLogModalLineItem.OfficerClockOut}
							user={user}
							positionId={jobActivityLogModalLineItem.PositionId}
							officerId={jobActivityLogModalLineItem.OfficerId}
							jobEventTimeZoneId={jobActivityLogModalLineItem.JobEventTimeZoneId}
							positionTitle={jobActivityLogModalLineItem.PositionTitle}
						/>
					)}

					<RefundNonRKPayInvoiceEstimateModal
						show={showRefundNonRKPayInvoiceEstimateModal}
						onCancel={() => setShowRefundNonRKPayInvoiceEstimateModal(false)}
						estimateId={query.id}
						onConfirm={() => handleRefundEstimate(query.id)}
						nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
					/>

					<ConnectedInvoicesModal
						show={showConnectedInvoicesModal}
						onHide={() => toggleConnectedInvoicesModal()}
						invoices={associatedInvoices}
						router={router}
					/>
				</div>
			</div>
		</DisputeProvider>
	);
};

NonRKPayInvoiceDetail.propTypes = {
	user: PropTypes.object,
	location: PropTypes.object,
	router: PropTypes.object,
	flags: PropTypes.object.isRequired,
};

NonRKPayInvoiceDetail.defaultProps = {
	user: {},
	location: {},
	router: {},
};

export default withLDConsumer()(NonRKPayInvoiceDetail);
